/* ClickstreamDashboard.jsx */
import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";

import PeriodSelect from "./PeriodSelect";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import DataTable from "examples/Tables/DataTable";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import crudsService from "services/cruds-service";

/* helpers ─────────────────────────────────────────────── */
const msToS = ms => Math.round(ms / 1000);
const pickColors = n => {
  const palette = [
    "info", "dark", "error", "secondary", "primary", "warning", "success",
  ];
  return Array.from({ length: n }, (_, i) => palette[i % palette.length]);
};

export default function ClickstreamDashboard() {
  const [period, setPeriod] = useState("yesterday");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  /* fetch doc once per period */
  useEffect(() => {
    setBusy(true);
    crudsService.getAggDaily(period)
      .then(r => setDoc(r.data[0].events_metrics))
      .finally(() => setBusy(false));
  }, [period]);

  /* page-views per 10 min line */
  const pvLine = useMemo(() => {
    const pv = doc?.timeseries?.page_views_per_10m ?? [];
    return {
      labels: pv.map((_, i) => i.toString()),
      datasets: [{ label: "Page views", color: "info", data: pv }],
    };
  }, [doc]);

  /* dwell per 10 min line */
  const dwellLine = useMemo(() => {
    const dw = doc?.timeseries?.avg_dwell_ms_per_10m ?? [];
    return {
      labels: dw.map((_, i) => i.toString()),
      datasets: [{ label: "Avg dwell-s", color: "warning", data: dw.map(msToS) }],
    };
  }, [doc]);

  /* hourly bar datasets */
  const barSeries = (ary, lbl, color) => ({
    labels: ary.map((_, h) => {
      const t = new Date(0, 0, 0, h, 0, 0);
      return `${t.toLocaleString("en-US", { hour: "numeric", hour12: true })}`;
    }),
    datasets: [{ label: lbl, color, data: ary }],
  });

  if (busy || !doc)
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );

  const { kpi, timeseries, pie, extras, generated_at } = doc;

  return (
    <>
      <MDBox mb={2}>
        {/* <PeriodSelect value={period} onChange={setPeriod} /> */}
      </MDBox>


      {/* ─── KPI tiles ───────────────────────── */}
      <MDTypography variant="h4">Headline KPIs</MDTypography>
      <MDTypography variant="button" >
        Data generated at: {new Date(generated_at).toLocaleDateString()}
      </MDTypography>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Page views" }} count={kpi.page_views} icon={{ component: "visibility", color: "info" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Avg Dwell Time (s)" }} count={msToS(kpi.avg_dwell_ms)} icon={{ component: "timer", color: "warning" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Bounce %" }} count={`${kpi.bounce_rate_pct}%`} icon={{ component: "trip_origin", color: "error" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Peak Users" }} count={kpi.peak_concurrent} icon={{ component: "people", color: "primary" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Attempts at viewing locked course material" }} count={kpi.locked_course_attempts} icon={{ component: "lock", color: "secondary" }} />
        </Grid>
      </Grid>

      {/* ─── 10-minute trends ────────────────── */}
      {/* <Divider />
      <MDTypography variant="h4">10-minute resolution (yesterday)</MDTypography>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={6}>
          <ReportsLineChart
            color="info"
            title="Page Views per 10 min"
            chart={pvLine}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReportsLineChart
            color="warning"
            title="Avg Dwell Time per 10 min"
            chart={dwellLine}
          />
        </Grid>
      </Grid> */}

      {/* ─── hourly bars ─────────────────────── */}
      <Divider />
      <MDTypography variant="h4">Hourly interactions (Yesterday)</MDTypography>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={3}>
          <VerticalBarChart
            icon={{ component: "filter_alt", color: "info" }}
            title="Free filter applied on courses"
            chart={barSeries(timeseries.free_filter_per_hour || [], "Free-filter", "info")}
            description="UTC hours"
            date={generated_at}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <VerticalBarChart
            icon={{ component: "search", color: "secondary" }}
            title="Search filter"
            chart={barSeries(timeseries.search_filter_per_hour || [], "Search", "secondary")}
            description="UTC hours"
            date={generated_at}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <VerticalBarChart
            icon={{ component: "login", color: "success" }}
            title="Logins"
            chart={barSeries(timeseries.app_visited_per_hour || [], "Visited", "success")}
            description="UTC hours"
            date={generated_at}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <VerticalBarChart
            icon={{ component: "logout", color: "error" }}
            title="Logouts"
            chart={barSeries(timeseries.app_left_per_hour || [], "Left", "error")}
            description="UTC hours"
            date={generated_at}
          />
        </Grid>
      </Grid>

      {/* ─── doughnuts ───────────────────────── */}
      <Divider />
      <MDTypography variant="h4">Page & device breakdown</MDTypography>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={3}>
          <DefaultDoughnutChart
            title="Page views (top)"
            chart={{
              labels: Object.keys(pie.page_view_by_page),
              datasets: {
                label: "pages",
                data: Object.values(pie.page_view_by_page),
                backgroundColors: pickColors(Object.keys(pie.page_view_by_page).length),
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DefaultDoughnutChart
            title="Device type"
            chart={{
              labels: Object.keys(pie.device),
              datasets: {
                label: "device",
                data: Object.values(pie.device),
                backgroundColors: pickColors(Object.keys(pie.device).length),
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DefaultDoughnutChart
            title="Browser"
            chart={{
              labels: Object.keys(pie.browser),
              datasets: {
                label: "browser",
                data: Object.values(pie.browser),
                backgroundColors: pickColors(Object.keys(pie.browser).length),
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DefaultDoughnutChart
            title="Country"
            chart={{
              labels: Object.keys(pie.country),
              datasets: {
                label: "country",
                data: Object.values(pie.country),
                backgroundColors: pickColors(Object.keys(pie.country).length),
              },
            }}
          />
        </Grid>
      </Grid>

      {/* ─── exit pages table ─────────────────── */}
      <Divider />
      <MDTypography variant="h4" mb={2}>Top exit pages</MDTypography>
      <DataTable
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        table={{
          columns: [
            { Header: "page", accessor: "page", id: "page", width: "70%" },
            { Header: "exits", accessor: "exits", id: "exits", width: "30%" },
          ],
          rows: extras.top_exit_pages,
        }}
      />
    </>
  );
}
